import { catchError, combineLatest, defaultIfEmpty, map, Observable, of } from 'rxjs';

import { FinnairLocation } from '@fcom/dapi/api/models/finnair-location';
import { isPresent } from '@fcom/core/utils/utils';
import { Location } from '@fcom/core-api';
import { TripType } from '@fcom/core/interfaces/triptype.interface';

import { HaulType } from '../interfaces/gtm.interface';

/**
 * List of short haul country codes
 * Taken from ancillaries microservice
 */
const shortHaulCountries: string[] = [
  'AT',
  'BE',
  'BG',
  'CH',
  'CY',
  'CZ',
  'DE',
  'DK',
  'ES',
  'FI',
  'FR',
  'GB',
  'GR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SE',
  'SK',
  'IL',
  'HR',
  'TR',
  'SI',
  'MT',
  'EE',
  'RU',
  'IS',
  'EG',
  'AL',
  'MA',
];

export const isShortHaulCountry = (countryCode: string): boolean =>
  shortHaulCountries.some((country) => country === countryCode?.toUpperCase());

export const isShortHaulFlight = (
  departureLocation: FinnairLocation | Location,
  arrivalLocation: FinnairLocation | Location
): boolean => {
  if (!isPresent(departureLocation) || !isPresent(arrivalLocation)) {
    return true;
  }
  return isShortHaulCountry(departureLocation.countryCode) && isShortHaulCountry(arrivalLocation.countryCode);
};

export const resolveHaulType = (
  locationService: { bestGuessFor: (query: string, locale: string) => Observable<Location> },
  origin: string,
  destination: string,
  locale: string
): Observable<HaulType | null> => {
  if (!origin || !destination) {
    return of(null);
  }
  return combineLatest([
    locationService.bestGuessFor(origin, locale),
    locationService.bestGuessFor(destination, locale),
  ]).pipe(
    map(([originLocation, destinationLocation]) =>
      isShortHaulFlight(originLocation, destinationLocation) ? HaulType.SHORT : HaulType.LONG
    ),
    catchError(() => of(null)),
    defaultIfEmpty(null)
  );
};

export const resolveTripTypeByHaul = (
  locationService: { bestGuessFor: (query: string, locale: string) => Observable<Location> },
  origin: string,
  destination: string,
  locale: string,
  fallback: TripType = TripType.ONEWAY
): Observable<TripType> =>
  resolveHaulType(locationService, origin, destination, locale).pipe(
    map((haulType) => {
      if (haulType === null) {
        return fallback;
      }
      return haulType === HaulType.SHORT ? TripType.ONEWAY : TripType.RETURN;
    })
  );
